import {
  type PropsWithChildren,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useLongPress } from 'use-long-press';
import { useEventListener, useResizeObserver } from 'usehooks-ts';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import useUserPlatform from '@/hooks/use-user-platform';
import useUserDisplayInfo from '@/hooks/users/use-user-display-info';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import { useEmbedLayout } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { EmbedLayout } from '@/utils/embed-layout';
import cn from '@/utils/merge-class-names';
import { isGroupMessage } from '@/utils/message';
import MessageDecoratorFooter from './message-decorator-footer';
import MessageDecoratorRole from './message-decorator-role';
import useMessageInfo from './use-message-info';

interface Props {
  message: ConversationMessageTypeUnion;
  showHeader?: boolean;
  onResize?: () => void;
}

export default function MessageDecorator({ message, showHeader = true, children, onResize }: PropsWithChildren<Props>) {
  const isGroup = isGroupMessage(message);
  const { canSummonContextMenu, isMine, isNotification, isStale, isGroupAnnouncement } = useMessageInfo(message);
  const {
    name: displayName,
    avatarUrl,
    role,
  } = useUserDisplayInfo(message.UserID, {
    groupId: isGroup ? message.GroupID : undefined,
    defaultName: message.UserNickName,
    defaultAvatar: message.UserHeadImg,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { supportsTouch, isDesktop } = useUserPlatform();
  const { resetSelectedMessages } = useConversationStore();
  const layout = useEmbedLayout();
  const { handleInteraction } = useConversationHandlers();
  const shouldShowHeader = Boolean(!isMine && isGroup && (!isNotification || isGroupAnnouncement) && showHeader);
  const shouldShowAvatar = Boolean(
    !isMine &&
      (!isNotification || isGroupAnnouncement) &&
      message.UserHeadImg &&
      showHeader &&
      layout > EmbedLayout.MINI,
  );

  const handleContextEvents = (
    e: MouseEvent | ReactMouseEvent<HTMLElement> | ReactPointerEvent<HTMLElement> | ReactTouchEvent<HTMLElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canSummonContextMenu) return;

    resetSelectedMessages([message]);
    const target = e.target as HTMLElement;

    let position: [number, number] = [0, 0];
    if ('clientX' in e) {
      position = [e.clientX, e.clientY];
    } else if ('touches' in e && e.touches.length > 0) {
      const touch = e.touches.item(0);
      position = [touch.clientX, touch.clientY];
    }

    if (target.tagName === 'A' && target.dataset.direct === 'false') {
      if (isDesktop) {
        // 定位桌面板的contextMenu
        handleInteraction('contextmenu', position);
      }
    } else if (!isGroup || hasClassName(target, 'group/message')) {
      handleInteraction('contextmenu', position);
    } else if (hasClassName(target, 'group/avatar')) {
      handleInteraction('clear');
      makePrompt({
        key: 'message-avatar-context',
        type: 'action-sheet',
        title: '',
        resolutionButtons: [{ label: t('conversation.mention.mention'), resolution: 'mention' }],
        meta: { position },
      }).then((result) => {
        if (result === 'mention') {
          handleInteraction('mention');
        }
      });
    }
  };

  const bindLongPress = useLongPress<HTMLDivElement>(
    (e) => {
      handleContextEvents(e);
    },
    {
      threshold: 600,
      filterEvents: () => {
        return supportsTouch;
      },
    },
  );

  useEventListener(
    'contextmenu',
    (e) => {
      handleContextEvents(e);
    },
    containerRef,
  );

  useResizeObserver({ ref: containerRef, onResize, box: 'border-box' });

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-1 items-start gap-2', isMine ? 'flex-row-reverse pl-5' : 'pr-5', {
        'opacity-50': message.Status === null && !isStale,
      })}
      {...bindLongPress()}>
      {shouldShowAvatar && (
        <Link
          to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({
            params: { userId: message.UserID },
            searchParams: {
              groupId: isGroup ? message.GroupID : undefined,
              source: 'avatar',
            },
          })}>
          <AvatarWithStatus
            imageUrl={avatarUrl}
            label={displayName}
            className='size-8 group/avatar'
            size={[32, 32]}
            defaultAvatar={isGroup ? 'group' : 'user'}
          />
        </Link>
      )}

      <div className='flex flex-col gap-1 w-fit desktop:max-w-[60%]'>
        {shouldShowHeader && (
          <div className='chat-header flex gap-1 items-center max-w-80vw min-w-1'>
            <span className='text-body2 desktop:text-sm truncate text-body'>{displayName}</span>

            {isGroup && <MessageDecoratorRole role={role} />}
          </div>
        )}

        <div className={cn('flex items-end gap-1 group/message', { 'flex-row-reverse': isMine })}>
          {children}

          {(!isNotification || isGroupAnnouncement) && <MessageDecoratorFooter message={message} />}
        </div>
      </div>
    </div>
  );
}

function hasClassName(element: Element | null | undefined, className: string): boolean {
  if (!element) return false;
  return element.classList.contains(className) || hasClassName(element.parentElement, className);
}
